import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, Circle, Loader2 } from "lucide-react";
import { apiService } from "@/lib/api-service";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface PaymentStatusToggleProps {
  overtimeEntryId: string;
  paymentStatus: boolean;
  partialPaymentStatus: boolean;
  approvalStatus: string;
  projectId: string;
  disabled?: boolean;
}

export function PaymentStatusToggle({
  overtimeEntryId,
  paymentStatus,
  partialPaymentStatus,
  approvalStatus,
  projectId,
  disabled = false,
}: PaymentStatusToggleProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const handleTogglePaymentStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isUpdating || disabled) return;

    setIsUpdating(true);
    try {
      await apiService.patch(`/overtime/${overtimeEntryId}/payment-status`, {
        paymentStatus: !paymentStatus,
      });

      // Invalidate and refetch relevant queries
      await queryClient.invalidateQueries({
        queryKey: ["overtime", "project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["projects"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project", projectId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["project-revision-history", projectId],
      });

      toast.success(
        `Payment status ${!paymentStatus ? "marked as paid" : "marked as unpaid"}`
      );
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    } finally {
      setIsUpdating(false);
    }
  };

  // Only show for approved overtime entries
  if (approvalStatus !== "APPROVED") {
    return null;
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleTogglePaymentStatus}
      disabled={disabled || isUpdating}
      className="h-auto py-1 px-1.5 hover:bg-muted rounded-full"
    >
      {isUpdating ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : paymentStatus ? (
        <div className="flex items-center gap-1">
          <CheckCircle className="h-4 w-4 text-green-600" />
          Paid
        </div>
      ) : partialPaymentStatus ? (
        <div className="flex items-center gap-1">
          <Circle className="h-4 w-4 text-yellow-600" />
          Partial
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <Circle className="h-4 w-4 text-muted-foreground" />
          Unpaid
        </div>
      )}
    </Button>
  );
}
